import { useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { EditorRico, iconoDeArchivo, leerEditor, pesoLegible } from '../novedades/piezas';
import { usePublicarEnComunidad, type Comunidad } from './api';

/* ── La tinta de la portada ────────────────────────────────────────────── */

/**
 * Qué color de texto se lee mejor sobre el color que eligió quien creó la
 * comunidad.
 *
 * El vanilla escribía **siempre en blanco** encima de un `<input type="color">`
 * sin límites. Con las dos comunidades de la base salía bien —5,87 y 14,26—
 * pero eso es suerte, no diseño: con un `#FFD700` el título quedaba en **1,40**
 * y nadie se enteraba hasta verlo.
 *
 * La fórmula es la luminancia relativa de WCAG; el umbral de 0,45 es donde el
 * contraste contra blanco y contra la tinta oscura se cruzan. Así el color
 * elegido sigue mandando en la portada y el texto se lee igual.
 */
export function tintaSobre(color: string): { texto: string; suave: string } {
  const hex = color.trim().replace('#', '');
  const completo = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(completo)) return { texto: '#fff', suave: 'rgba(255,255,255,.85)' };
  const canal = (i: number) => {
    const v = parseInt(completo.slice(i, i + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luz = 0.2126 * canal(0) + 0.7152 * canal(2) + 0.0722 * canal(4);
  return luz > 0.45
    ? { texto: '#10202f', suave: 'rgba(16,32,47,.78)' }
    : { texto: '#fff', suave: 'rgba(255,255,255,.85)' };
}

/* ── La tarjeta de la lista ────────────────────────────────────────────── */

/**
 * Una comunidad en la rejilla.
 *
 * **Toda la tarjeta entra, con una sola parada de Tab.** El botón es el nombre
 * y su `::after` se estira sobre la tarjeta entera. `text-inherit` no es
 * decoración: la hoja se importa sin `preflight`, así que un `<button>` se
 * queda con el color del navegador —1,44 de contraste en modo oscuro, medido
 * el 2026-09-17 en la tarjeta de evento— en vez de heredar el del título.
 */
export function TarjetaDeComunidad({ c }: { c: Comunidad }) {
  const tinta = tintaSobre(c.color);

  return (
    <Card interactiva className="relative flex w-full flex-col overflow-hidden">
      <div
        className="flex h-28 shrink-0 items-end justify-between gap-2 p-3"
        style={{ background: c.color, color: tinta.texto }}
      >
        <i
          className={cn('bi text-8 leading-none opacity-90', c.icon || 'bi-people')}
          aria-hidden="true"
        />
        <div className="flex flex-wrap justify-end gap-1.5">
          {c.is_private && (
            <span
              className="flex items-center gap-1 rounded-full px-2 py-0.5 text-1 font-semibold"
              style={{ background: 'rgba(0,0,0,.28)', color: '#fff' }}
            >
              <i className="bi bi-lock-fill" aria-hidden="true" /> Privada
            </span>
          )}
          {c.is_member && (
            <span
              className="flex items-center gap-1 rounded-full px-2 py-0.5 text-1 font-semibold"
              style={{ background: 'rgba(0,0,0,.28)', color: '#fff' }}
            >
              <i className="bi bi-check-lg" aria-hidden="true" /> Miembro
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="text-4 font-bold leading-snug text-brand-ink [overflow-wrap:anywhere]">
          {/* Un enlace de hash y no un botón con `APP.navigate`: el router
              actualiza el hash con `replaceState`, así que una navegación por
              botón NO deja entrada en el historial y el botón Atrás del
              navegador se saltaría la lista. El enlace sí la deja, y es lo que
              hacía el vanilla. */}
          <a
            href={`#espacios/${c.id}`}
            className="rounded-xs text-left text-inherit no-underline after:absolute after:inset-0 focus-visible:focus-anillo"
          >
            {c.name}
          </a>
        </h3>

        {c.description && (
          <p className="line-clamp-2 text-2 text-muted [overflow-wrap:anywhere]">{c.description}</p>
        )}

        <ul className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-2 text-ink-2">
          <li className="flex items-center gap-1.5">
            <i className="bi bi-people" aria-hidden="true" />
            {c.member_count} {c.member_count === 1 ? 'miembro' : 'miembros'}
          </li>
          <li className="flex items-center gap-1.5">
            <i className="bi bi-chat-dots" aria-hidden="true" />
            {c.post_count} {c.post_count === 1 ? 'publicación' : 'publicaciones'}
          </li>
        </ul>

        {c.area_name && (
          <p className="flex items-center gap-1.5 text-1 text-muted">
            <i className="bi bi-diagram-2" aria-hidden="true" /> {c.area_name}
          </p>
        )}
      </div>
    </Card>
  );
}

/* ── El compositor ─────────────────────────────────────────────────────── */

/** El mismo tope que aplica el servidor sobre el HTML (`content.py`). */
const MAX_CARACTERES = 10000;
const AVISO_DESDE = 0.8;
const MAX_ADJUNTO_MB = 25;

/**
 * Escribir en el muro de una comunidad.
 *
 * Usa el **mismo editor** que el muro y no un `<textarea>` plano como el
 * vanilla, y el motivo es que la tarjeta que lo pinta después también es la
 * misma: escribir en plano en una caja cuyo resultado se renderiza con formato
 * es prometer una cosa y enseñar otra.
 *
 * La casilla de comunicado solo se ofrece a quien modera **y** el servidor la
 * vuelve a comprobar: si llega sin ese rol se ignora en silencio y la
 * publicación sale normal. Ocultarla aquí es cortesía, no la autorización.
 */
export function CompositorDeComunidad({ comunidad }: { comunidad: Comunidad }) {
  const editorRef = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const [texto, setTexto] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);
  const [exigirLectura, setExigirLectura] = useState(false);
  const [version, setVersion] = useState(0);
  const publicar = usePublicarEnComunidad();

  const largo = texto.length;
  const pasado = largo > MAX_CARACTERES;

  function elegirArchivos(lista: FileList | null) {
    if (!lista) return;
    const buenos: File[] = [];
    for (const f of Array.from(lista)) {
      if (f.size > MAX_ADJUNTO_MB * 1024 * 1024) {
        Utils.toast(`«${f.name}» pesa más de ${MAX_ADJUNTO_MB} MB`, 'warning');
        continue;
      }
      buenos.push(f);
    }
    setArchivos((previos) => [...previos, ...buenos]);
    if (entrada.current) entrada.current.value = '';
  }

  function enviar() {
    const { html, texto: plano } = leerEditor(editorRef.current);
    if (!plano) {
      Utils.toast('Escribe algo para publicar', 'warning');
      return;
    }
    if (pasado) {
      Utils.toast(`El texto pasa de ${MAX_CARACTERES.toLocaleString('es-CO')} caracteres`, 'warning');
      return;
    }
    // El aviso amable antes de mandarlo: el servidor valida contra la misma
    // lista y devolvería un 400 seco.
    if (Utils.findProfanity(plano)) {
      Utils.toast('Tu publicación contiene lenguaje no permitido. Por favor edítala.', 'error', 4500);
      return;
    }

    publicar.mutate(
      { comunidadId: comunidad.id, contenido: html, exigirLectura, archivos },
      {
        onSuccess: (r) => {
          if (r.fallidos.length) {
            Utils.toast(
              `Se publicó, pero ${r.fallidos.length} ${r.fallidos.length === 1 ? 'adjunto no se pudo subir' : 'adjuntos no se pudieron subir'}`,
              'warning',
            );
          } else if (r.requires_ack) {
            Utils.toast(
              r.avisados > 0
                ? `Comunicado publicado. Avisados ${r.avisados} miembros, que tendrán que confirmar la lectura.`
                : 'Comunicado publicado. Los miembros deberán confirmar que lo leyeron.',
              'success',
              4500,
            );
          } else {
            Utils.toast('Publicado', 'success');
          }
          setTexto('');
          setArchivos([]);
          setExigirLectura(false);
          setVersion((n) => n + 1);
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo publicar', 'error'),
      },
    );
  }

  return (
    <section
      aria-label="Publicar en la comunidad"
      className="mb-4 rounded-md border border-line bg-surface p-3 focus-within:border-brand-ink"
    >
      <div ref={editorRef}>
        <EditorRico
          etiqueta={`Qué quieres compartir con ${comunidad.name}`}
          placeholder="Comparte algo con la comunidad…"
          version={version}
          onCambio={setTexto}
          // La lista de moderación se pide al enfocar, no al abrir la pantalla:
          // quien solo entra a leer no se la descarga. `loadProfanityList` es
          // idempotente.
          onFoco={() => void Utils.loadProfanityList()}
        />
      </div>

      {archivos.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {archivos.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center gap-1.5 rounded-sm bg-sunken px-2 py-1 text-1 text-ink-2"
            >
              <i className={cn('bi', iconoDeArchivo(f.type, f.name))} aria-hidden="true" />
              <span className="max-w-48 truncate">{f.name}</span>
              <span className="text-muted">{pesoLegible(f.size)}</span>
              <button
                type="button"
                onClick={() => setArchivos((p) => p.filter((_, j) => j !== i))}
                className="rounded-xs p-0.5 text-muted hover:text-danger-ink focus-visible:focus-anillo"
                aria-label={`Quitar ${f.name}`}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => entrada.current?.click()}>
            <i className="bi bi-paperclip" aria-hidden="true" /> Adjuntar
          </Button>
          <input
            ref={entrada}
            type="file"
            multiple
            hidden
            accept=".pdf,.xlsx,.xls,.csv,.txt,.doc,.docx,image/*,application/pdf"
            onChange={(e) => elegirArchivos(e.target.files)}
          />
          {largo > MAX_CARACTERES * AVISO_DESDE && (
            <span
              className={cn('text-1 tabular-nums', pasado ? 'text-danger-ink' : 'text-muted')}
              aria-live="polite"
            >
              {largo.toLocaleString('es-CO')} / {MAX_CARACTERES.toLocaleString('es-CO')}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {comunidad.can_moderate && (
            <label className="flex min-h-11 cursor-pointer items-center gap-2 text-2 text-ink-2">
              <input
                type="checkbox"
                checked={exigirLectura}
                onChange={(e) => setExigirLectura(e.target.checked)}
                className="size-4 accent-brand"
              />
              <i className="bi bi-megaphone-fill text-brand-ink" aria-hidden="true" />
              Comunicado — pedir confirmación de lectura
            </label>
          )}
          <Button onClick={enviar} loading={publicar.isPending} disabled={pasado}>
            <i className="bi bi-send-fill" aria-hidden="true" /> Publicar
          </Button>
        </div>
      </div>

      {exigirLectura && (
        <p className="mt-2 flex items-start gap-1.5 text-1 text-muted">
          <i className="bi bi-info-circle mt-0.5 shrink-0" aria-hidden="true" />
          Cada miembro tendrá que confirmar que lo leyó, y le llegará al buzón. Una vez alguien
          confirme, la publicación ya no se podrá retirar.
        </p>
      )}
    </section>
  );
}
